import {
  Card,
  Checkbox,
  Col,
  Button,
  ErrorNotification,
  Heading1,
  Heading4,
  Margin,
  P,
  Row,
  Stack,
  Strong,
  TextField,
  TextLink,
  Ul,
  Li,
} from "@ovotech/element";
import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import styled, { useTheme } from "styled-components";

import hypervolt from "_assets/hypervolt.png";
import tethered from "_assets/tethered.png";
import untethered from "_assets/untethered.png";
import andersena2MidnightBlack from "_assets/andersen/ANDERSEN-A2-MIDNIGHTBLACK-MIDNIGHTBLACK.png";
import andersena2CheltenhamGraphite from "_assets/andersen/ANDERSEN-A2-CHELTENHAMGRAPHITE-CHELTENHAMGRAPHITE.png";
import andersena2CotswoldOak from "_assets/andersen/ANDERSEN-A2-COTSWOLDOAK-CORNISHCREAM.png";
import andersena3MidnightBlack from "_assets/andersen/ANDERSEN-A3-MIDNIGHTBLACK-MIDNIGHTBLACK.png";
import andersena3CheltenhamGraphite from "_assets/andersen/ANDERSEN-A3-CHELTENHAMGRAPHITE-CHELTENHAMGRAPHITE.png";
import andersena3CotswoldOak from "_assets/andersen/ANDERSEN-A3-COTSWOLDOAK-CORNISHCREAM.png";
import andersenQuartzTetheredCheltenhamGraphite from "_assets/andersen/ANDERSEN-QUARTZ-TETHERED-CHELTENHAMGRAPHITE-CHELTENHAMGRAPHITE.png";
import andersenQuartzTetheredMidnightBlack from "_assets/andersen/ANDERSEN-QUARTZ-TETHERED-MIDNIGHTBLACK-MIDNIGHTBLACK.png";
import andersenQuartzTetheredCotswoldOak from "_assets/andersen/ANDERSEN-QUARTZ-TETHERED-COTSWOLDOAK-CASHMERE.png";
import andersenQuartzUntetheredCheltenhamGraphite from "_assets/andersen/ANDERSEN-QUARTZ-SOCKETED-CHELTENHAMGRAPHITE-CHELTENHAMGRAPHITE.png";
import andersenQuartzUntetheredMidnightBlack from "_assets/andersen/ANDERSEN-QUARTZ-SOCKETED-MIDNIGHTBLACK-MIDNIGHTBLACK.png";
import andersenQuartzUntetheredCotswoldOak from "_assets/andersen/ANDERSEN-QUARTZ-SOCKETED-COTSWOLDOAK-CASHMERE.png";
import zappi21TetheredBlack from "_assets/myEnergi/MyEnergi_Zappi_2.1_Tethered_Black.png";
import zappi21TetheredWhite from "_assets/myEnergi/MyEnergi_Zappi_2.1_Tethered_White.png";
import zappi21UntetheredBlack from "_assets/myEnergi/MyEnergi_Zappi_2.1_Untethered_Black.png";
import zappi21UntetheredWhite from "_assets/myEnergi/MyEnergi_Zappi_2.1_Untethered_White.png";
import zappiGLOTetheredBlack from "_assets/myEnergi/MyEnergi_ZappiGLO_Tethered_Black.png";
import { AnalyticsEvents } from "_core/analytics/AnalyticsEvents";
import Mixpanel from "_core/analytics/Mixpanel";
import { Page } from "_core/components";
import { Flex } from "_core/components/Flex";
import { LoadingSkeleton } from "_core/components/LoadingSkeleton";
import { PostcodeModal } from "_core/components/PostcodeModal";
import { FormApi, Formy } from "_core/form";
import { useStore } from "_core/store";
import {
  Charger,
  ErrorTypes,
  ProductUrls,
  QuoteDetailsResponse,
  TakePaymentRequest,
  TakePaymentResponse,
} from "_core/types";
import { fetchClient } from "_core/utils";
import { useConfig } from "_core/utils/config";
import { fetchQuoteDetails } from "_core/utils/fetchQuoteDetails";
import { FormValues, PaymentValidator } from "_core/validators/Payment";
import { useSearchParams } from "react-router-dom";
import { useFeature } from "_core/utils/optimizely";
import { useMediaQuery } from "usehooks-ts";

type ChargerInformation = {
  chargerName: string;
  chargerImage: string | null;
};
const TextLinkStyled = styled(TextLink)`
  font-weight: 400;
  cursor: pointer;
`;

const Form = styled.form`
  padding-top: 1.2rem;
`;

const ReducedMarginP = styled(P)`
  margin-top: 0.1rem;
  margin-bottom: 0.1rem;
`;

const Value = (props: { value: any }) => (
  <div>
    <P data-dd-privacy="mask">{props.value}</P>
    <Margin left={3} />
  </div>
);

const chargerData: { [key: string]: ChargerInformation } = {
  hypervolt: {
    chargerName: "Hypervolt Home 3 Pro",
    chargerImage: hypervolt,
  },
  "indra untethered": {
    chargerName: "Indra Smart Pro Untethered",
    chargerImage: untethered,
  },
  "indra socketed": {
    chargerName: "Indra Smart Pro Socketed",
    chargerImage: untethered,
  },
  "indra tethered": {
    chargerName: "Indra Smart Pro Tethered",
    chargerImage: tethered,
  },
  "andersen a2 midnight black": {
    chargerName: "Andersen A2 - Midnight Black / Midnight Black",
    chargerImage: andersena2MidnightBlack,
  },
  "andersen a2 cheltenham graphite": {
    chargerName: "Andersen A2 - Cheltenham Graphite / Cheltenham Graphite",
    chargerImage: andersena2CheltenhamGraphite,
  },
  "andersen a2 cotswold oak": {
    chargerName: "Andersen A2 - Cotswold Oak / Cornish Cream",
    chargerImage: andersena2CotswoldOak,
  },
  "andersen a3 midnight black": {
    chargerName: "Andersen A3 - Midnight Black / Midnight Black",
    chargerImage: andersena3MidnightBlack,
  },
  "andersen a3 cheltenham graphite": {
    chargerName: "Andersen A3 - Cheltenham Graphite / Cheltenham Graphite",
    chargerImage: andersena3CheltenhamGraphite,
  },
  "andersen a3 cotswold oak": {
    chargerName: "Andersen A3 - Cotswold Oak / Cornish Cream",
    chargerImage: andersena3CotswoldOak,
  },
  "andersen quartz untethered midnight black": {
    chargerName:
      "Andersen Quartz - Untethered - Midnight Black / Midnight Black",
    chargerImage: andersenQuartzUntetheredMidnightBlack,
  },
  "andersen quartz untethered cheltenham graphite": {
    chargerName:
      "Andersen Quartz - Untethered - Cheltenham Graphite / Cheltenham Graphite",
    chargerImage: andersenQuartzUntetheredCheltenhamGraphite,
  },
  "andersen quartz untethered cotswold oak": {
    chargerName: "Andersen Quartz - Untethered - Cotswold Oak / Cashmere",
    chargerImage: andersenQuartzUntetheredCotswoldOak,
  },
  "andersen quartz tethered midnight black": {
    chargerName: "Andersen Quartz - Tethered - Midnight Black / Midnight Black",
    chargerImage: andersenQuartzTetheredMidnightBlack,
  },
  "andersen quartz tethered cheltenham graphite": {
    chargerName:
      "Andersen Quartz - Tethered - Cheltenham Graphite / Cheltenham Graphite",
    chargerImage: andersenQuartzTetheredCheltenhamGraphite,
  },
  "andersen quartz tethered cotswold oak": {
    chargerName: "Andersen Quartz - Tethered - Cotswold Oak / Cashmere",
    chargerImage: andersenQuartzTetheredCotswoldOak,
  },
  "myenergi zappi 2.1 untethered black": {
    chargerName: "myenergi zappi 2.1 - Untethered - Black",
    chargerImage: zappi21UntetheredBlack,
  },
  "myenergi zappi 2.1 untethered white": {
    chargerName: "myenergi zappi 2.1 - Untethered - White",
    chargerImage: zappi21UntetheredWhite,
  },
  "myenergi zappi 2.1 tethered black": {
    chargerName: "myenergi zappi 2.1 - Tethered - Black",
    chargerImage: zappi21TetheredBlack,
  },
  "myenergi zappi 2.1 tethered white": {
    chargerName: "myenergi zappi 2.1 - Tethered - White",
    chargerImage: zappi21TetheredWhite,
  },
  "myenergi zappi glo tethered black": {
    chargerName: "myenergi zappi GLO - Tethered - Black",
    chargerImage: zappiGLOTetheredBlack,
  },
};

const getChargerInformation = (
  lineItems: Partial<QuoteDetailsResponse["quote"]["lineItems"]> = []
): ChargerInformation => {
  const charger = lineItems.find((obj) => {
    if (!obj?.description) return false;
    const lowerCaseDescription = obj.description.toLowerCase();
    return (
      lowerCaseDescription.includes(Charger.INDRA) ||
      lowerCaseDescription.includes(Charger.HYPERVOLT) ||
      lowerCaseDescription.includes(Charger.ANDERSENA2.replace("-", " ")) ||
      lowerCaseDescription.includes(Charger.ANDERSENA3.replace("-", " ")) ||
      lowerCaseDescription.includes(Charger.ANDERSENQUARTZ.replace("-", " ")) ||
      lowerCaseDescription.includes(Charger.ZAPPI21.replace("-", " ")) ||
      lowerCaseDescription.includes(Charger.ZAPPIGLOW.replace("-", " "))
    );
  });

  if (!charger || !charger.description) {
    return { chargerName: "", chargerImage: null };
  }
  const description = charger.description.toLowerCase();
  const foundKey = Object.keys(chargerData).find((key) => {
    const keyParts = key.split(" ");
    return keyParts.every((part) => description.includes(part));
  });

  return foundKey
    ? chargerData[foundKey]
    : { chargerName: "", chargerImage: null };
};

const PaymentPageContent = () => {
  // even though the store will parse this out of the url, with our current design we need this data
  // synchronously otherwise we will redirect to WE_CANT_FIND_ORDER
  const [searchParams] = useSearchParams();
  const externalId = searchParams.get("externalId");

  const theme = useTheme();
  const isMediumBreakpoint = useMediaQuery(
    `(max-width: ${theme.breakpoint.medium})`
  );

  const [isEditingBillingAddress, setIsEditingBillingAddress] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [postcode, setPostcode] = useState("");
  const [showModal, setShowModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [maintenanceModeEnabled] = useFeature("ev_charger_maintenance_mode");

  const { API_URL } = useConfig();
  const {
    setQuoteDetails,
    quoteDetails,
    cableSetup,
    setPostcode: setStorePostcode,
  } = useStore();
  const navigate = useNavigate();

  const formatPrice = (price: number | undefined): string | undefined => {
    if (!price) return;

    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(price);
  };

  const vatRate = quoteDetails?.quote?.vatRate;
  const vatTotal = formatPrice(quoteDetails?.quote?.vatTotal);
  const installationTotal = formatPrice(
    quoteDetails?.quote?.lineItems.reduce((acc, cur) => acc + cur.amount, 0)
  );
  const totalAmount = formatPrice(quoteDetails?.quote.totalAmount);
  const { chargerName, chargerImage } = getChargerInformation(
    quoteDetails?.quote.lineItems
  );
  const chargerType = chargerName.split(" ")[0].toLowerCase();

  useEffect(() => {
    setLoading(true);

    if (!externalId) {
      console.error("External id not provided");
      navigate(ProductUrls.ErrorPage, {
        state: { error: ErrorTypes.WE_CANT_FIND_ORDER },
      });
      return;
    }

    if (maintenanceModeEnabled) {
      navigate(ProductUrls.ErrorPage, {
        state: { error: ErrorTypes.MAINTENANCE },
      });
      return;
    }

    if (postcode && externalId) {
      setStorePostcode(postcode);
      fetchQuoteDetails(
        {
          externalId,
          postcode,
        },
        setLoading,
        API_URL,
        setQuoteDetails,
        navigate
      );
    }

    return () => {
      setLoading(false);
    };
  }, [
    API_URL,
    externalId,
    maintenanceModeEnabled,
    navigate,
    postcode,
    setQuoteDetails,
    setStorePostcode,
    showModal,
  ]);

  const handleSubmit = async (values: FormValues) => {
    Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
      buttonId: "submission",
      pageName: ProductUrls.Payment,
      cableSetup,
      chargerType,
      consentGiven: values.consent,
      privacyPolicyAccepted: values.privacyPolicy,
    });

    try {
      setLoading(true);

      const payload: TakePaymentRequest = {
        // we know that it is a string, because if it is not the customer gets redirected
        // in the useEffect above
        externalId: externalId as string,
        postcode: values.postcode,
        billingAddress: {
          addressLine1: values.billingAddressLine1,
          addressLine2: values.billingAddressLine2,
          city: values.billingCity,
          postcode: values.billingPostcode,
        },
      };

      const { url } = await fetchClient<TakePaymentResponse>({
        base: API_URL,
        path: "/v1/payment/take-payment",
        options: {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      });

      if (url) {
        window.location.href = url;
      }
    } catch (e) {
      setSubmitError(true);
    } finally {
      setLoading(false);
    }
  };

  return loading ? (
    <>
      <PostcodeModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        setPostcode={setPostcode}
        postcode={""}
      />
      <LoadingSkeleton />
    </>
  ) : (
    <Formy
      initialValues={{
        addressLine1: quoteDetails?.installationAddress.addressLine1,
        addressLine2: quoteDetails?.installationAddress.addressLine2,
        billingAddressLine1: quoteDetails?.installationAddress.addressLine1,
        billingAddressLine2: quoteDetails?.installationAddress.addressLine2,
        billingAddressSame: false,
        billingCity: quoteDetails?.installationAddress.town,
        billingEmail: quoteDetails?.email,
        billingPhone: quoteDetails?.phoneNumber,
        billingPostcode: quoteDetails?.installationAddress.postCode,
        city: quoteDetails?.installationAddress.town,
        email: quoteDetails?.email,
        firstName: quoteDetails?.firstName,
        lastName: quoteDetails?.lastName,
        phone: quoteDetails?.phoneNumber,
        postcode: quoteDetails?.installationAddress.postCode,
        privacyPolicy: false,
        consent: false,
      }}
      validationSchema={PaymentValidator}
      onSubmit={handleSubmit}
    >
      {({
        setFieldValue,
        handleSubmit,
        values,
        errors,
        touched,
        setFieldTouched,
      }: FormApi<FormValues>) => {
        return (
          <div>
            <Heading1>Your charger and install payment</Heading1>
            <Margin top={6} />
            <P size="lg">
              Get ready to enjoy cheaper charging on your doorstep.
            </P>

            {submitError && (
              <ErrorNotification
                id="payment-error"
                title="We couldn’t process your payment"
                style={{ marginBottom: "4rem" }}
              >
                <P>
                  We can’t process your payment. Please select a different
                  payment method or check your payment details including your
                  billing address.
                </P>
              </ErrorNotification>
            )}

            <Margin top={6} />

            <Row isNested>
              <Col
                small={12}
                medium={8}
                style={{ order: isMediumBreakpoint ? 1 : 0 }}
              >
                <Card style={{ marginBottom: 32 }}>
                  <Heading4 className={"SurfaceHeading"}>Your details</Heading4>
                  <Margin top={6} />
                  <Row isNested>
                    <Col small={12} medium={4}>
                      <Flex>
                        <div>
                          <Strong>Your address</Strong>
                          <Margin bottom={1} />
                          <>
                            <ReducedMarginP data-dd-privacy="mask">
                              {values.firstName} {values.lastName}
                            </ReducedMarginP>
                            <ReducedMarginP data-dd-privacy="mask">
                              {values.addressLine1}
                            </ReducedMarginP>
                            <ReducedMarginP data-dd-privacy="mask">
                              {values.addressLine2}
                            </ReducedMarginP>
                            <ReducedMarginP data-dd-privacy="mask">
                              {values.city}
                            </ReducedMarginP>
                            <ReducedMarginP data-dd-privacy="mask">
                              {values.postcode}
                            </ReducedMarginP>
                          </>
                        </div>
                      </Flex>
                    </Col>
                    <Col small={12} medium={4}>
                      <Strong>Phone number</Strong>
                      <Margin bottom={1} />
                      <Value value={values.phone} />
                    </Col>
                    <Col
                      small={12}
                      medium={4}
                      style={{ overflowWrap: "anywhere" }}
                    >
                      <Strong>Email address</Strong>
                      <Margin bottom={1} />
                      <Value value={values.email} />
                    </Col>
                    {!quoteDetails?.paid ? (
                      <Col small={12} style={{ order: 1 }}>
                        <Checkbox
                          checked={values.billingAddressSame}
                          onChange={() => {
                            setFieldValue(
                              "billingAddressSame",
                              !values.billingAddressSame
                            );

                            setFieldValue(
                              "billingAddressLine1",
                              values.addressLine1
                            );
                            setFieldValue(
                              "billingAddressLine2",
                              values.addressLine2
                            );
                            setFieldValue("billingCity", values.city);
                            setFieldValue("billingEmail", values.email);
                            setFieldValue("billingPhone", values.phone);
                            setFieldValue("billingPostcode", values.postcode);

                            setIsEditingBillingAddress(false);
                          }}
                          id="billingAddressSame"
                          name="billingAddressSame"
                          data-testid="billing-address"
                          onClick={() =>
                            Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
                              buttonId: "billingAddressSame",
                            })
                          }
                          label={
                            <ReducedMarginP
                              style={{
                                fontWeight: 600,
                                marginTop: "-0.5rem",
                              }}
                            >
                              My billing address is the same as my install
                              address.{" "}
                              <TextLinkStyled
                                style={{
                                  cursor: values.billingAddressSame
                                    ? "default"
                                    : "pointer",
                                  color: values.billingAddressSame
                                    ? "grey"
                                    : undefined,
                                  userSelect: "none",
                                }}
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  ev.stopPropagation();
                                  setIsEditingBillingAddress(true);
                                }}
                              >
                                Update my billing address.
                              </TextLinkStyled>
                            </ReducedMarginP>
                          }
                        />
                        {isEditingBillingAddress && !values.billingAddressSame && (
                          <div data-testid="billing-address-fields">
                            <Margin top={4} />
                            <Stack spaceBetween={4}>
                              <TextField
                                disabled={values.billingAddressSame}
                                id="billing-address-line1"
                                name="billingAddressLine1"
                                label="Address"
                                error={errors.billingAddressLine1}
                                value={values.billingAddressLine1}
                                onClick={() =>
                                  Mixpanel.track(
                                    AnalyticsEvents.CLICKEDBUTTON,
                                    {
                                      buttonId: "billing-address-line1",
                                    }
                                  )
                                }
                                onChange={(
                                  ev: ChangeEvent<HTMLInputElement>
                                ) => {
                                  setFieldValue(
                                    "billingAddressLine1",
                                    ev.target.value
                                  );
                                }}
                              />
                              <TextField
                                disabled={values.billingAddressSame}
                                id="billing-address-line2"
                                name="billingAddressLine2"
                                label="Address"
                                error={errors.billingAddressLine2}
                                value={
                                  values.billingAddressLine2
                                    ? values.billingAddressLine2
                                    : ""
                                }
                                onChange={(
                                  ev: ChangeEvent<HTMLInputElement>
                                ) => {
                                  setFieldValue(
                                    "billingAddressLine2",
                                    ev.target.value
                                  );
                                }}
                              />
                              <TextField
                                disabled={values.billingAddressSame}
                                id="billing-city"
                                name="billingCity"
                                label="City"
                                error={errors.billingCity}
                                value={values.billingCity}
                                onClick={() =>
                                  Mixpanel.track(
                                    AnalyticsEvents.CLICKEDBUTTON,
                                    {
                                      buttonId: "billing-city",
                                    }
                                  )
                                }
                                onChange={(
                                  ev: ChangeEvent<HTMLInputElement>
                                ) => {
                                  setFieldValue("billingCity", ev.target.value);
                                }}
                              />
                              <TextField
                                disabled={values.billingAddressSame}
                                id="billing-postcode"
                                name="billingPostcode"
                                label="Postcode"
                                error={errors.billingPostcode}
                                value={values.billingPostcode}
                                onClick={() =>
                                  Mixpanel.track(
                                    AnalyticsEvents.CLICKEDBUTTON,
                                    {
                                      buttonId: "billing-postcode",
                                    }
                                  )
                                }
                                onChange={(
                                  ev: ChangeEvent<HTMLInputElement>
                                ) => {
                                  setFieldValue(
                                    "billingPostcode",
                                    ev.target.value
                                  );
                                }}
                              />
                            </Stack>
                          </div>
                        )}
                      </Col>
                    ) : null}
                  </Row>
                </Card>

                {!quoteDetails?.paid && (
                  <Card style={{ marginBottom: 32 }}>
                    <Heading4 className={"SurfaceHeading"}>
                      Important information
                    </Heading4>
                    <Margin top={4} />
                    <Form data-testid="payment">
                      <div>
                        <Checkbox
                          data-testid="consent"
                          error={touched.consent && errors.consent}
                          id="consent"
                          name="consent"
                          checked={values.consent}
                          onBlur={() => setFieldTouched("consent")}
                          onClick={() =>
                            Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
                              buttonId: "consent",
                              action: values.consent ? "unclicked" : "clicked",
                            })
                          }
                          onChange={() =>
                            setFieldValue("consent", !values.consent)
                          }
                          label={
                            <>
                              <span>I agree to the OVO </span>

                              <TextLink
                                href="https://www.ovoenergy.com/terms/ev-charger-terms-v1"
                                target="_blank"
                              >
                                Terms
                              </TextLink>

                              <span> and the </span>

                              <TextLink
                                href="https://www.ovoenergy.com/privacy-policy"
                                target="_blank"
                              >
                                OVO Energy Privacy Policy
                              </TextLink>
                            </>
                          }
                        />
                      </div>
                      <div>
                        <Checkbox
                          data-testid="privacyPolicy"
                          name="privacyPolicy"
                          id="privacyPolicy"
                          label={
                            <span>
                              I acknowledge that the price I&apos;m paying now
                              may be subject to change depending on whether any
                              additional works are required in order to install
                              the charger following a site visit by an engineer
                            </span>
                          }
                          checked={values.privacyPolicy}
                          error={touched.privacyPolicy && errors.privacyPolicy}
                          onBlur={() => setFieldTouched("privacyPolicy")}
                          onClick={() =>
                            Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
                              buttonId: "privacyPolicy",
                              action: values.privacyPolicy
                                ? "unclicked"
                                : "clicked",
                            })
                          }
                          onChange={() =>
                            setFieldValue(
                              "privacyPolicy",
                              !values.privacyPolicy
                            )
                          }
                        />
                      </div>
                    </Form>
                  </Card>
                )}

                {!quoteDetails?.paid ? (
                  <div style={{ textAlign: "right" }}>
                    <Button
                      data-testid="confirm-payment"
                      fullWidth={isMediumBreakpoint}
                      onClick={(event) =>
                        handleSubmit(
                          event as unknown as FormEvent<HTMLFormElement>
                        )
                      }
                    >
                      Confirm your payment
                    </Button>
                  </div>
                ) : (
                  <Heading4 className={"SurfaceHeading"}>
                    Thank you for your payment.
                  </Heading4>
                )}
              </Col>

              <Col
                small={12}
                medium={4}
                style={{
                  order: isMediumBreakpoint ? 0 : 1,
                  marginBottom: isMediumBreakpoint ? 16 : 0,
                }}
              >
                <Card>
                  <Heading4 className={"SurfaceHeading"}>
                    {chargerName}
                  </Heading4>
                  <Margin top={5} />
                  {chargerImage && (
                    <div style={{ textAlign: "center" }}>
                      <img
                        alt={`Image of ${chargerName}`}
                        src={chargerImage}
                        width={150}
                      />
                    </div>
                  )}
                  <Margin top={5} />

                  <div>
                    <P>
                      <Strong>Charger & installation</Strong>
                    </P>
                    <Margin top={3} />
                    <Ul>
                      {quoteDetails?.quote.lineItems.map((item) => (
                        <Li key={item.name}>
                          <Flex
                            justifyContent="space-between"
                            style={{ gap: 16 }}
                          >
                            <span>{item.description}</span>
                            <span>
                              <Strong>{formatPrice(item.amount)}</Strong>
                            </span>
                          </Flex>
                        </Li>
                      ))}
                    </Ul>
                  </div>

                  <Flex justifyContent="space-between">
                    <P size={"md"}>Installation total</P>
                    <P size={"md"}>
                      <Strong>{installationTotal}</Strong>
                    </P>
                  </Flex>

                  <Flex justifyContent="space-between">
                    <P size={"md"}>VAT (at {vatRate ? vatRate * 100 : 0}%)</P>
                    <P size={"md"}>
                      <Strong>{vatTotal}</Strong>
                    </P>
                  </Flex>

                  <Margin top={5} />

                  <Flex alignItems="center" justifyContent="space-between">
                    <P size="lg">
                      <Strong>Total</Strong>
                    </P>
                    <P size="lg">
                      <Strong>{totalAmount}</Strong>
                    </P>
                  </Flex>
                </Card>
              </Col>
            </Row>
          </div>
        );
      }}
    </Formy>
  );
};

export const PaymentPage = () => {
  return (
    <Page url={ProductUrls.Payment}>
      <PaymentPageContent />
    </Page>
  );
};
